"use client";

import type { ILink } from "@components/core/Link";
import type React from "react";

import { BaseLink } from "@components/core/Link";
import { isValidLinkWithLabel } from "@ui/lib/link";
import { cn } from "@ui/lib/utils";
import { usePathname } from "next/navigation";
import { useContext, useState } from "react";
import { useEffect, useRef } from "react";

import { Logo } from "../core/Logo";
import { LinkTracked } from "../core/LinkTracked";
import { EventName } from "@mw/shared/analyticsEventNames";
import { FEATURE_FLAGS } from "@/app/_analytics/feature-flags";
import { useLoadingFeatureFlagEnabled } from "@/app/_analytics/posthog-client";
import { useTranslations } from "next-intl";
import { MainTemplateContext } from "@/app/_template/MainTemplateContext";
import type { ICmsPlatform } from "@mw/shared/payload-types";
import { PlatformBadge } from "./PlatformBadge";
import { MainMenu, MobileMenu } from "./NavigationBar";
import { clsx } from "clsx";
import { Button as AriaButton } from "react-aria-components";

interface ContextualNavigationProps {
  children?: React.ReactNode;
  className?: string;
  ctas?: ILink[] | null;
  /** @deprecated No longer rendered — platform badge is shown instead */
  icon?: unknown;
  showSecondaryCtaOnMobile?: boolean;
  /** @deprecated No longer rendered — platform badge is shown instead */
  title?: string;
  trackerClass?: string;
  platform?: ICmsPlatform;
}

export function ContextualNavigationTracker() {
  return <div className='tracker-contextual-nav -mt-1 h-1 w-full' />;
}

const CONTACT_SALES_CTA_HREF = "/contact-sales";

const ContextualNavigationBase = ({
  className,
  ctas,
  showSecondaryCtaOnMobile = false,
  children,
  trackerClass = "tracker-contextual-nav",
  platform: platformProp,
}: ContextualNavigationProps) => {
  const { platform: platformFromContext, secondaryNav, platformLink } = useContext(MainTemplateContext);
  const platform = platformProp || platformFromContext;
  const t = useTranslations("ClientKeys.Globals");
  const trackerEnd = useRef<HTMLDivElement>(null);
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);

  const [navReveal, setNavReveal] = useState(false);
  const [trackerOutView, setTrackerOutView] = useState(false);

  const pathname = usePathname();

  // Use a ref to track trackerOutView so IntersectionObserver callback has current value
  const trackerOutViewRef = useRef(trackerOutView);
  trackerOutViewRef.current = trackerOutView;

  useEffect(() => {
    // Set CSS variable for contextual nav height
    document.documentElement.style.setProperty("--contextual-nav-height", "64px");

    let IO: IntersectionObserver | null = null;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let mounted = true;

    const setupObserver = (tracker: Element) => {
      IO = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.target === tracker) {
              if (entry.boundingClientRect.top < 0) {
                setNavReveal(true);
                setTrackerOutView(true);
              } else {
                setNavReveal(false);
                setTrackerOutView(false);
              }
            }

            if (entry.target === trackerEnd.current && trackerOutViewRef.current) {
              if (pathname?.startsWith("/pricing")) {
                return;
              }
              if (entry.boundingClientRect.top < 0) {
                setNavReveal(false);
              } else if (entry.isIntersecting && entry.boundingClientRect.top > 0) {
                setNavReveal(true);
              }
            }
          });
        },
        { root: null, rootMargin: "0px", threshold: 1 },
      );

      IO.observe(tracker);
      if (trackerEnd.current) {
        IO.observe(trackerEnd.current);
      }
    };

    // Try to find the tracker element with retries
    const findAndObserve = (attempt = 0) => {
      if (!mounted) return;

      const trackers = document.querySelectorAll(`.${trackerClass}`);
      const tracker = trackers[trackers.length - 1];

      if (tracker) {
        setupObserver(tracker);
      } else if (attempt < 5) {
        // Retry up to 5 times with increasing delays
        retryTimeout = setTimeout(() => findAndObserve(attempt + 1), 50 * (attempt + 1));
      }
    };

    // Use requestAnimationFrame to ensure DOM is painted before searching
    requestAnimationFrame(() => {
      findAndObserve();
    });

    return () => {
      mounted = false;
      if (retryTimeout) clearTimeout(retryTimeout);
      if (IO) IO.disconnect();
      document.documentElement.style.removeProperty("--contextual-nav-height");
    };
  }, [trackerClass, pathname]);

  const ctaLinks =
    ctas && ctas?.length > 0 ? (
      <ul className='tw-pointer-events-auto flex items-center gap-x-8'>
        {ctas?.filter(isValidLinkWithLabel).map((cta, index) => {
          const isSecondary = ctas.length > 1 && index === 0;
          const isPrimary = !isSecondary;
          const eventName = isSecondary ? EventName.mw_nav_secondary_link : EventName.mw_nav_primary_link;
          const variant = isSecondary ? "elevated" : "primary";
          return (
            <li key={index}>
              {(isPrimary || (isSecondary && showSecondaryCtaOnMobile)) && (
                <LinkTracked
                  asButton
                  buttonVariants={{ size: "sm", variant }}
                  className='sm:tw-hidden'
                  {...cta}
                  eventName={eventName}
                />
              )}
              <LinkTracked
                asButton
                buttonVariants={{ size: "md", variant }}
                className='tw-hidden sm:tw-inline-flex'
                {...cta}
                eventName={eventName}
              />
            </li>
          );
        })}
      </ul>
    ) : null;

  return (
    <div className=''>
      <div
        aria-hidden={!navReveal}
        className={cn(
          "tw-fixed tw-top-0 tw-z-[9998] tw-flex tw-h-16 tw-items-center container-reset breakout",
          "tw-duration-300 tw-ease-in-out tw-bg-white",
          navReveal ? "tw-translate-y-0" : "tw-invisible -tw-translate-y-full",
          className,
        )}
        style={{ transitionProperty: "transform, visibility" }}
      >
        <div className='tw-absolute tw-inset-x-0 tw-h-px tw-bottom-0 tw-bg-gray-200' />
        <div
          className={clsx(
            "tw-flex tw-items-center tw-justify-between tw-gap-1 container",
            mobileMenuIsOpen && "tw-opacity-0",
          )}
        >
          <div className='tw-gap-4 xl:tw-gap-6 tw-flex tw-items-center'>
            {platform === "default" ? (
              <BaseLink
                href='/'
                aria-label={t("elevenlabs")}
                className='tw-h-9 tw-flex tw-items-center tw-px-3 -tw-mx-3 tw-rounded-full tw-outline-none focus-visible:tw-outline-focus'
              >
                <Logo />
              </BaseLink>
            ) : (
              <>
                <div className={clsx(secondaryNav.length > 0 ? "tw-hidden lg:tw-contents" : "tw-contents")}>
                  <PlatformBadge platform={platform} href={platformLink} />
                </div>
                {secondaryNav.length > 0 && (
                  <>
                    <MobileMenu
                      logoHref='/'
                      nav={secondaryNav}
                      isOpen={mobileMenuIsOpen}
                      setIsOpen={setMobileMenuIsOpen}
                      className='tw-mt-1'
                      renderTopBar={({ isActive, onClose }) => (
                        <div
                          className={clsx(
                            "tw-fixed tw-top-0 tw-inset-x-0 tw-flex tw-justify-between tw-pointer-events-none px-20 md:px-40",
                            !isActive && "tw-opacity-0",
                          )}
                        >
                          <button
                            onClick={onClose}
                            className='tw-h-16 tw-flex tw-items-center tw-gap-0.5 tw-pointer-events-auto'
                            aria-label={t("navigation.close_secondary", {
                              platform: { creative: "ElevenCreative", agents: "ElevenAgents", api: "ElevenAPI" }[
                                platform
                              ],
                            })}
                          >
                            <PlatformBadge platform={platform} />
                            <svg
                              viewBox='0 0 24 24'
                              fill='none'
                              className='tw-size-5 tw-relative tw-top-0.125 tw-text-black/50'
                              aria-hidden
                            >
                              <path
                                d='M8 14.0001L11.2929 10.7072C11.6834 10.3167 12.3166 10.3167 12.7071 10.7072L16 14.0001'
                                stroke='currentColor'
                                strokeWidth='1.8'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </button>
                          {ctaLinks}
                        </div>
                      )}
                    >
                      {({ ref }) => (
                        <AriaButton
                          ref={ref}
                          className={clsx(
                            "tw-relative tw-rounded-full tw-outline-none tw-p-1.5 -tw-m-1.5 -tw-mr-2.5 focus-visible:tw-outline-focus lg:tw-hidden tw-flex tw-items-center tw-h-16 tw-gap-0.5 tw-text-black",
                          )}
                          aria-label={t("navigation.open_secondary", {
                            platform: { creative: "ElevenCreative", agents: "ElevenAgents", api: "ElevenAPI" }[
                              platform
                            ],
                          })}
                        >
                          <PlatformBadge platform={platform} />
                          <svg
                            viewBox='0 0 24 24'
                            fill='none'
                            className='tw-size-5 tw-relative tw-top-0.25 tw-text-black/50'
                            aria-hidden
                          >
                            <path
                              d='M8 10L11.2929 13.2929C11.6834 13.6834 12.3166 13.6834 12.7071 13.2929L16 10'
                              stroke='currentColor'
                              strokeWidth='1.8'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </AriaButton>
                      )}
                    </MobileMenu>
                    <MainMenu nav={secondaryNav} platform='default' includeLogo={false} closeOnScroll />
                  </>
                )}
              </>
            )}
          </div>
          {ctaLinks}
        </div>
      </div>
      <div className='relative'>
        {children}
        <div aria-hidden className='absolute inset-x-0 bottom-[50vh] h-1 w-full' ref={trackerEnd} />
      </div>
    </div>
  );
};

function ContextualNavigationWithContactSalesCta(props: ContextualNavigationProps) {
  const t = useTranslations("ClientKeys.Globals");
  const { enabled: showContactSalesCta } = useLoadingFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_NAV_CTA_TEST, {
    variantKey: "test",
  });
  const ctas = showContactSalesCta
    ? [{ href: CONTACT_SALES_CTA_HREF, label: t("contact_sales") }, ...(props.ctas ?? [])]
    : props.ctas;

  return <ContextualNavigationBase {...props} ctas={ctas} />;
}

const ContextualNavigation = (props: ContextualNavigationProps) => {
  const shouldEvaluateContactSalesCtaFlag =
    props.ctas?.length === 1 && !String(props.ctas[0]?.href || "").includes(CONTACT_SALES_CTA_HREF);

  if (!shouldEvaluateContactSalesCtaFlag) {
    return <ContextualNavigationBase {...props} />;
  }

  return <ContextualNavigationWithContactSalesCta {...props} />;
};

ContextualNavigationBase.displayName = "ContextualNavigationBase";
ContextualNavigationWithContactSalesCta.displayName = "ContextualNavigationWithContactSalesCta";
ContextualNavigation.displayName = "ContextualNavigation";

export { ContextualNavigation };
